import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { LogoVariant } from '@/constants/modules';
import { Icon, type IconName } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { useRemoveLogoMutation, useUploadLogoMutation } from '@/hooks/useBrandingMutations';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { MEDIA_IMAGE_CONTENT_TYPES, buildAcceptAttribute } from '@/constants/media';

const LOGO_ACCEPT_ATTRIBUTE =
  buildAcceptAttribute(MEDIA_IMAGE_CONTENT_TYPES) + ',image/svg+xml,.svg';

interface LogoCardProps {
  variant: LogoVariant;
  logoUrl: string | null;
}

const VARIANT_ICON: Record<LogoVariant, IconName> = {
  full: 'Monitor',
  tablet: 'Tablet',
  mobile: 'Smartphone',
  icon: 'Image',
};

export function LogoCard({ variant, logoUrl }: LogoCardProps) {
  const { t } = useTranslation('modules');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadLogoMutation();
  const removeMutation = useRemoveLogoMutation();
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);

  const variantName = t('branding.variant.' + variant + 'Name');
  const isBusy = uploadMutation.isPending || removeMutation.isPending;

  function openFilePicker() {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click();
    }
  }

  function handleFileSelected(changeEvent: React.ChangeEvent<HTMLInputElement>) {
    const files = changeEvent.target.files;
    if (files === null) {
      return;
    }
    const selectedFile = files.item(0);
    if (selectedFile === null) {
      return;
    }
    uploadMutation.mutate(
      { variant, file: selectedFile },
      {
        onSuccess: function notifyUploaded() {
          showSuccessNotification(t('branding.uploaded'));
        },
        onError: function notifyError(uploadError) {
          const backendMessage = resolveApiErrorMessage(uploadError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t('branding.uploadError')
          );
        },
      }
    );
    changeEvent.target.value = '';
  }

  function confirmRemove() {
    removeMutation.mutate(variant, {
      onSuccess: function notifyRemoved() {
        showSuccessNotification(t('branding.removed'));
        setIsConfirmingRemove(false);
      },
      onError: function notifyError(removeError) {
        const backendMessage = resolveApiErrorMessage(removeError);
        showErrorNotification(
          backendMessage.length > 0 ? backendMessage : t('branding.uploadError')
        );
        setIsConfirmingRemove(false);
      },
    });
  }

  return (
    <Card padding="none" shadow={false} className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon name={VARIANT_ICON[variant]} className="text-zooviolet" />
        <div className="min-w-0">
          <div className="text-sm font-bold text-zooindigo">{variantName}</div>
          <div className="text-[11px] text-zoomuted">{t('branding.variant.' + variant + 'Hint')}</div>
        </div>
      </div>

      <div className="h-28 rounded-xl bg-zoovpalelavender border border-zooborder flex items-center justify-center overflow-hidden mb-3">
        {logoUrl === null ? (
          <div className="flex flex-col items-center gap-1 text-zoomuted">
            <Icon name="Image" />
            <span className="text-[11px]">{t('branding.emptyPreview')}</span>
          </div>
        ) : (
          <img
            src={logoUrl}
            alt={variantName}
            className="max-h-24 max-w-full object-contain"
          />
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="chip"
          disabled={isBusy}
          isLoading={uploadMutation.isPending}
          onClick={openFilePicker}
          className="flex-1 gap-1.5"
        >
          {uploadMutation.isPending ? null : <Icon name="Upload" />}
          {logoUrl === null ? t('branding.upload') : t('branding.replace')}
        </Button>
        {logoUrl === null ? null : (
          <Button
            variant="destructiveGhost"
            size="icon"
            disabled={isBusy}
            aria-label={t('branding.remove')}
            title={t('branding.remove')}
            onClick={function requestRemove() {
              setIsConfirmingRemove(true);
            }}
          >
            <Icon name="Trash" />
          </Button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={LOGO_ACCEPT_ATTRIBUTE}
        className="hidden"
        onChange={handleFileSelected}
      />

      {isConfirmingRemove === true ? (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('branding.removeConfirmTitle')}
          description={t('branding.removeConfirmDescription', { variant: variantName })}
          confirmLabel={t('branding.remove')}
          isConfirming={removeMutation.isPending}
          onConfirm={confirmRemove}
          onCancel={function cancelRemove() {
            setIsConfirmingRemove(false);
          }}
        />
      ) : null}
    </Card>
  );
}
